import React, { useMemo, useContext } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiCorePackage } from '@wix/da-papi-types';
import Checkbox from '@wix/da-ds/pkg/Checkbox';
import PointsIcon from '@wix/da-ds/pkg/Icons/Points';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { type PackagePickerStoreSlice } from '../../../../../redux/packagePicker/types';

import s from './BillingCyclePackage.scss';

interface Props {
  pkg: PapiCorePackage;
  sale: PackagePickerStoreSlice['sale'];
  hasDiscount?: boolean;
  isGift?: boolean;
  isSelected?: boolean;
  onSelect: (interval: string) => void;
}

const BillingCyclePackage: React.FC<Props> = ({
  pkg,
  sale,
  hasDiscount,
  isGift,
  isSelected,
  onSelect,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isYearly = pkg.interval === 'year';

  const cycleText = useMemo(() => {
    const monthlyCost = formatDollars(
      hasDiscount
        ? pkg.monthlyPrice
        : pkg.monthlyPriceBeforeDiscount ?? pkg.monthlyPrice
    );
    let i18nKey;

    if (isGift) {
      i18nKey = isYearly
        ? 'packagePicker.billingCycle.yearBreakdown.gift'
        : 'packagePicker.billingCycle.everyMonth.gift';
    } else if (hasDiscount) {
      i18nKey = isYearly
        ? 'packagePicker.billingCycle.yearBreakdown.sale'
        : 'packagePicker.billingCycle.everyMonth.sale';
    } else {
      i18nKey = isYearly
        ? 'packagePicker.billingCycle.yearBreakdown'
        : 'packagePicker.billingCycle.everyMonth';
    }

    return isYearly ? t(i18nKey, { amount: monthlyCost }) : t(i18nKey);
  }, [pkg, isGift, hasDiscount, isYearly, t]);

  return (
    <Checkbox
      className={cn(s['cycle'], isSelected && s['selected'])}
      labelClassName={s['cycle-label']}
      isChecked={isSelected}
      onChange={isChecked => {
        if (isChecked) {
          onSelect(pkg.interval);
        }
      }}
    >
      <div className={s['price-row']}>
        {hasDiscount ? (
          <>
            <span className={s['dollar-price']}>
              {formatDollars(pkg.price)}
            </span>
            <span className={s['price-before']}>
              {formatDollars(pkg.priceBeforeDiscount)}
            </span>
          </>
        ) : (
          <span className={s['dollar-price']}>
            {formatDollars(pkg.priceBeforeDiscount ?? pkg.price)}
          </span>
        )}
        {!isMobile && (
          <>
            <span className={s['price-divider']}>/</span>
            <PointsIcon
              className={s['points-icon']}
              size={IconSize.MEDIUM}
              disableDefaultColors
            />
            <span className={s['points-price']}>
              {t('packagePicker.billingCycle.pointsPrice', {
                points: hasDiscount
                  ? pkg.pricePoints
                  : pkg.pointsPriceBeforeDiscount ?? pkg.pricePoints,
              })}
            </span>
          </>
        )}
        {hasDiscount && (
          <span className={s['savings']}>
            {t('packagePicker.coreOffer.saleBadge', {
              discountPercent: pkg.discountPercent,
            })}
          </span>
        )}
        {!sale && pkg.bulkPriceSaving && (
          <span className={s['savings']}>
            {t('packagePicker.billingCycle.bulkSavings', {
              amount: formatDollars(pkg.bulkPriceSaving),
            })}
          </span>
        )}
      </div>
      <span className={s['cycle-name']}>{cycleText}</span>
    </Checkbox>
  );
};
BillingCyclePackage.displayName = 'BillingCyclePackage';

export default BillingCyclePackage;
